import React, { useEffect, useState } from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import { format } from "date-fns";
import classNames from "classnames";
import { CircleCheck, Info } from "lucide-react";

interface DayViewProps {
  weekDays: any[];
  times: string[];
  activeDay: string;
  setActiveDay: (_day: string) => void;
  statusMap: Record<string, any>;
  handleCellClick: (_date: Date, _time: string) => void;
  handleSlotClick: (_slotId: string) => void;
  isCellClickable: (_date: Date, _time: string) => boolean;
  setSelectedAppointment: (_appointment: any) => void;
  setShowAppointmentDialog: (_show: boolean) => void;
}

const MappingClassByStatus = {
  disabled: {
    background: "bg-[#e0e0e0]",
    opacity: "opacity-50",
  },
  pending: {
    background: "bg-orange-100 border-orange-500 border-2",
    text: "overflow-hidden text-orange-600",
  },
  approved: {
    background: "bg-green-100 border-green-500 border-2",
    text: "text-green-600",
  },
  available: {
    background: "bg-white",
  },
};

interface IAppointmentInfoProps {
  appointment: any;
  title: string;
  startTime: string;
  endTime: string;
  status: "pending" | "approved";
  isDisabled?: boolean;
  setSelectedAppointment: (_appointment?: any) => void;
  setShowAppointmentDialog: (_show: boolean) => void;
}
const AppointmentInfo: React.FC<IAppointmentInfoProps> = ({
  appointment,
  title,
  startTime,
  endTime,
  status,
  isDisabled,
  setSelectedAppointment,
  setShowAppointmentDialog,
}) => {
  const tooltipText = `${format(new Date(startTime), "HH:mm dd/MM/yyyy")} - ${format(new Date(endTime), "HH:mm dd/MM/yyyy")}\n${title}`;
  return (
    <Box
      key={startTime}
      className={classNames(
        `border border-gray-200 h-[50px] rounded relative`,
        {
          "opacity-50 cursor-not-allowed": isDisabled,
          [MappingClassByStatus.disabled.opacity]: isDisabled,
          [MappingClassByStatus.disabled.background]: isDisabled,
        },
        MappingClassByStatus[status]?.background ?? ""
      )}
    >
      <Tooltip title={tooltipText} className="absolute top-0 left-0">
        <Box
          className={classNames(
            "absolute inset-x-0 rounded text-xs p-2 z-10 flex flex-col justify-center",
            {
              "opacity-50 cursor-not-allowed": isDisabled,
              "overflow-hidden font-medium min-h-[48px] top-0": true,
            },
            MappingClassByStatus[status].text
          )}
          sx={{
            height: "50px",
            fontSize: "0.875rem",
            textAlign: "left",
            overflow: "hidden",
            maxWidth: "100%",
          }}
          onClick={(e) => {
            if (isDisabled) {
              e.preventDefault();
              e.stopPropagation();
              return;
            }
            e.stopPropagation();
            setSelectedAppointment(appointment);
            setShowAppointmentDialog(true);
          }}
        >
          <div className="flex w-full">
            <div className="flex flex-col flex-1">
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "380px",
                  display: "inline-block",
                  fontWeight: "600",
                }}
                title={title}
              >
                {title}
              </span>
              <p
                className="text-xs mt-1 line-clamp-1"
                title={appointment.description || "Individual Therapy"}
              >
                {appointment.description || "Individual Therapy"}
              </p>
            </div>

            <div className="flex items-center justify-center flex-shrink-0 ml-2">
              {status === "approved" ? (
                <CircleCheck size={20} className="text-white fill-green-600" />
              ) : (
                <Info
                  size={20}
                  className="text-white fill-orange-600 rotate-180"
                />
              )}
            </div>
          </div>
        </Box>
      </Tooltip>
    </Box>
  );
};

interface ISlotProps {
  slotId?: string;
  startTime?: string;
  endTime?: string;
  sessionDuration: number;
  isDisabled?: boolean;
  isAvailable?: boolean;
  onClick: (_slotId: string) => void;
}

const Slot: React.FC<ISlotProps> = ({
  slotId,
  onClick,
  isAvailable = true,
  startTime,
  sessionDuration: _sessionDuration,
  isDisabled = false,
}) => {
  return (
    <Box
      key={startTime}
      className={classNames(
        `border border-gray-200 h-[50px] rounded relative`,
        {
          "opacity-50 cursor-not-allowed": isDisabled,
          [MappingClassByStatus.disabled.opacity]: isDisabled,
          [MappingClassByStatus.disabled.background]: !slotId || isDisabled,
        }
      )}
    >
      {slotId && (
        <Box
          className={classNames(
            "absolute inset-x-0 rounded text-xs p-1 z-10",
            {
              "opacity-50 cursor-not-allowed": isDisabled,
              "overflow-hidden whitespace-nowrap font-medium min-h-[48px] top-0": true,
              "flex items-center justify-center": true,
            },
            { [MappingClassByStatus.available.background]: isAvailable }
          )}
          sx={{
            height: "50px",
          }}
          onClick={(e) => {
            if (isDisabled) {
              e.preventDefault();
              e.stopPropagation();
              return;
            }
            e.stopPropagation();
            slotId && onClick(slotId);
          }}
        ></Box>
      )}
    </Box>
  );
};

const DayView: React.FC<DayViewProps> = ({
  weekDays,
  times,
  activeDay,
  setActiveDay,
  statusMap,
  handleSlotClick,
  setSelectedAppointment,
  setShowAppointmentDialog,
}) => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  // Function to calculate current time position
  const getCurrentTimePosition = () => {
    const now = currentTime;
    const hours = now.getHours();
    const minutes = now.getMinutes();

    // Find the closest time slots
    const timeValues = times.map((time) => {
      const [hour, minute] = time.split(":").map(Number);
      return hour * 60 + minute;
    });

    const currentMinutes = hours * 60 + minutes;
    const slotHeight = 50;

    if (currentMinutes < timeValues[0]) {
      return 0; // Before first slot
    }

    if (currentMinutes > timeValues[timeValues.length - 1]) {
      return timeValues.length * slotHeight; // After last slot
    }

    // Find position between time slots
    for (let i = 0; i < timeValues.length - 1; i++) {
      if (
        currentMinutes >= timeValues[i] &&
        currentMinutes < timeValues[i + 1]
      ) {
        const progress =
          (currentMinutes - timeValues[i]) /
          (timeValues[i + 1] - timeValues[i]);
        const position = (i + progress) * slotHeight;
        return position;
      }
    }

    return 0;
  };

  return (
    <Box className="block md:hidden">
      <Box
        className="flex flex-nowrap gap-1 md:gap-4 lg:gap-6 mb-2 justify-center overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        {weekDays.map((day) => {
          const isToday = day.fullDate === format(new Date(), "yyyy-MM-dd");
          const isSelected = day.dayName === activeDay;
          return (
            <Box
              key={day.fullDate}
              className={
                `flex flex-col items-center justify-center px-2 py-2 mb-2 rounded-2xl min-w-[48px] md:min-w-[60px] lg:min-w-[72px]` +
                (isToday
                  ? " text-orange-500 bg-white shadow-lg"
                  : isSelected
                    ? " bg-orange-500 text-white shadow-lg"
                    : " bg-white text-gray-700 shadow-lg")
              }
              style={{ cursor: "pointer" }}
              onClick={() => setActiveDay(day.dayName)}
            >
              <span
                className={
                  "font-normal text-sm uppercase tracking-wide underline underline-offset-4" +
                  (isToday
                    ? " text-orange-500"
                    : isSelected
                      ? " text-white"
                      : " text-gray-700")
                }
                style={{ letterSpacing: "1px" }}
              >
                {day.dayName}
              </span>
              <span className="font-bold text-md mt-1 ">
                {day.displayDate.split(" ")[1]}
              </span>
            </Box>
          );
        })}
      </Box>
      <Box
        className="grid relative"
        style={{ gridTemplateColumns: "60px 1fr" }}
      >
        {times.map((time) => {
          const currentDayObj = weekDays.find((d) => d.dayName === activeDay);
          const key = `${currentDayObj?.fullDate} ${time}`;
          const item = statusMap[key];
          const now = new Date();
          const isPastSlot = item ? new Date(item?.startTime) < now : false;
          const isUnavailable = item ? item?.status !== "available" : false;
          const startDate = `${currentDayObj?.fullDate}T${time}`;
          const endDateObj = new Date(startDate);
          endDateObj.setMinutes(endDateObj.getMinutes() + 30);
          const endDate = format(endDateObj, "yyyy-MM-dd'T'HH:mm");

          return (
            <Box
              component="div"
              key={`mobile-${time}`}
              sx={{ display: "contents" }}
            >
              <Typography
                variant="body2"
                className="border border-gray-200 p-2 text-base text-right pr-3 font-medium min-w-[60px] max-w-[60px]"
              >
                {time}
              </Typography>
              {(() => {
                if (item?.appointmentId) {
                  // Approved appointments are always clickable and not blurred
                  const isApproved = item.status === "approved";
                  let isDisabled = false;
                  if (!isApproved) {
                    isDisabled = isPastSlot;
                  }
                  return (
                    <AppointmentInfo
                      title={item.title}
                      key={item.appointmentId}
                      appointment={item}
                      startTime={startDate}
                      endTime={endDate}
                      status={item.status}
                      setSelectedAppointment={setSelectedAppointment}
                      setShowAppointmentDialog={setShowAppointmentDialog}
                      isDisabled={isDisabled}
                    />
                  );
                }
                const isDisabled = isPastSlot || isUnavailable;
                return (
                  <Slot
                    onClick={() => handleSlotClick(item?.slotId)}
                    key={key}
                    slotId={item?.slotId}
                    startTime={startDate}
                    endTime={endDate}
                    sessionDuration={30}
                    isAvailable={item?.status === "available"}
                    isDisabled={isDisabled || isPastSlot}
                  />
                );
              })()}
            </Box>
          );
        })}

        {/* Current time indicator */}
        {(() => {
          const todayStr = format(new Date(), "yyyy-MM-dd");
          const currentDayObj = weekDays.find((d) => d.dayName === activeDay);
          if (currentDayObj?.fullDate !== todayStr) return null;
          return (
            <Box
              className="absolute z-20"
              sx={{
                top: `${getCurrentTimePosition() + 2}px`,
                width: "calc(100% - 60px)",
                right: 0,
                pointerEvents: "none",
                height: "2px",
                backgroundColor: "red",
              }}
            >
              <Box
                className="rounded-full bg-red-500 absolute"
                sx={{
                  width: "6px",
                  height: "6px",
                  left: "-3px",
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              />
            </Box>
          );
        })()}
      </Box>
    </Box>
  );
};

export default React.memo(DayView);
